import { auth } from '@/firebase/auth';
import { serverLogger } from "@/utils/logging";
import * as Sentry from '@sentry/nextjs';

type MetricType = {
  name: string;
  value: number;
  tags?: Record<string, string>;
};

class Monitoring {
  private static async getUserContext() {
    try {
      const currentUser = auth.currentUser;

      if (currentUser) {
        return {
          id: currentUser.uid ?? undefined,
          email: currentUser.email ?? undefined,
        };
      }

      return null;
    } catch (error) {
      serverLogger.error('Error getting user context:', error);
      return null;
    }
  }

  private static async enrichContext(context?: Record<string, any>) {
    try {
      const userContext = await this.getUserContext();

      if (!userContext) {
        return context;
      }

      return {
        ...context,
        user: userContext,
      };
    } catch (error) {
      serverLogger.error('Error enriching context:', error);
      return context;
    }
  }

  static async trackMetric(metric: MetricType) {
    try {
      const userContext = await this.getUserContext();
      const tags = {
        environment: process.env.NODE_ENV,
        userId: userContext?.id,
        userEmail: userContext?.email,
        ...metric.tags,
      };

      Sentry.addBreadcrumb({
        category: 'metrics',
        message: `${metric.name}: ${metric.value}`,
        data: tags,
        level: 'info',
      });

      if (userContext) {
        Sentry.setUser(userContext);
      }

      serverLogger.info(`Tracking metric: ${metric.name}`, { metric, tags, userContext });

      // You can also send to your custom metrics endpoint
      // if (process.env.METRICS_ENDPOINT) {
      //   await fetch(process.env.METRICS_ENDPOINT, {
      //     method: 'POST',
      //     headers: { 'Content-Type': 'application/json' },
      //     body: JSON.stringify({
      //       ...metric,
      //       tags,
      //       user: userContext
      //     }),
      //   });
      // }
    } catch (error) {
      serverLogger.error('Error tracking metric:', error);

      // Track the error in monitoring the metrics
      await this.trackError(error as Error, {
        metricName: metric.name,
        metricValue: metric.value,
        originalTags: metric.tags,
      });
    }
  }

  static async trackError(error: Error, context?: Record<string, any>) {
    try {
      const enrichedContext = await this.enrichContext(context);
      const userContext = await this.getUserContext();

      serverLogger.error(error, { enrichedContext, userContext });

      Sentry.withScope((scope) => {
        if (userContext) {
          scope.setUser(userContext);
        }

        if (enrichedContext) {
          scope.setExtras(enrichedContext);
        }
        
        scope.setTags({
          environment: process.env.NODE_ENV,
          userId: userContext?.id,
          userEmail: userContext?.email,
          errorType: error.name,
        });

        Sentry.captureException(error);
      });
    } catch (monitoringError) {
      serverLogger.error('Error in Monitoring.trackError:', monitoringError);
      serverLogger.error(error);
      Sentry.captureException(error);
    }
  }

  static async setUserContext(forceRefresh = false) {
    try {
      if (forceRefresh) {
        await auth.currentUser?.reload();
      }

      const userContext = await this.getUserContext();

      if (userContext) {
        Sentry.setUser(userContext);
        return true;
      }

      return false;
    } catch (error) {
      serverLogger.error('Error setting user context:', error);
      return false;
    }
  }

  static clearUserContext() {
    Sentry.setUser(null);
  }
}

export default Monitoring;